import { FieldGroup } from "@pdfking/components"
import type { AnyNode, ArrayForm, AtomForm, ObjectForm } from "@pdfking/forms"
import { FormType } from "@pdfking/forms"
import { CreateRenderFunction } from "@pdfking/reactivity/preact"
import {
  FullMethodDispatcher,
  Items,
  KeyTake,
  Keys,
  Length,
  RecordCreate,
  Values,
} from "@pdfking/tfunc"
import type { JSX } from "preact"

import { IDMixin } from "../../mixins/IDMixin"
import { FileDestination } from "../custom/FileDestination"
import { FileId } from "../custom/FileId"
import { FileRange } from "../custom/FileRange"
import { FileScale } from "../custom/FileScale"
import { DjLabel } from "../fields/DjLabel"
import { DjSelect } from "../fields/DjSelect"
import { FieldMixin } from "../mixins/FieldMixin"
import { ArrayMixin, EnumMixin, FormMixin, ObjectMixin } from "../mixins/FormMixin"
import { NodeMixin } from "../mixins/NodeMixin"
import { ArrayControls } from "./ArrayControls"
import { AtomField } from "./AtomField"
import { ChoiceField } from "./ChoiceField"
import { dom_id, child_path } from "./DomId"
import { col_span, FORM_STACK, is_visible, line_columns, starts_line } from "./FieldLayout"
import { FieldShell } from "./FieldShell"
import { ViewMixin } from "./ViewMixin"

const LABEL_KEY = "label"

// A field that owns its whole line: nested forms, arrays and multi-select
// button groups. Everything else is an inline field that shares a
// line grid with its neighbours.
const is_block = (node: AnyNode): boolean =>
  node.enum === FormType.ARRAY ||
  node.enum === FormType.OBJECT ||
  node.enum === FormType.ENUM ||
  (node.enum === FormType.CHOICE && !!(node as { multiple?: boolean }).multiple)

// Keyed dispatch: the preset schema fixes each key's node subtype (file_id →
// atom, file_destination → choice, ...) — a guarantee the type system can't
// see, so the registry widens each controller to the shared dispatch props.
const CUSTOM_CONTROLLERS = {
  file_id: FileId,
  file_range: FileRange,
  file_scale: FileScale,
  file_destination: FileDestination,
} as Partial<Record<string, (props: Record<string, unknown>) => JSX.Element>>

const is_leaf = (node: AnyNode) => node.enum === FormType.ATOM || node.enum === FormType.CHOICE

const inputs = (node: ArrayForm<unknown>): unknown[] => node.results.map(child => child.input)

// ObjectField/ArrayField recurse back into Dispatch, so the three stay in
// this file; the leaf fields and the shared shell live in their own.

const ObjectField = CreateRenderFunction({
  mixins: [NodeMixin, FieldMixin, ViewMixin, ObjectMixin] as const,
  render() {
    const node = this.node!
    const dispatch = (key: string, child: AnyNode, cell: boolean) => (
      <Dispatch
        cell={cell}
        className={this.className}
        compact_mode={this.compact_mode}
        ctx={this.ctx}
        disabled={this.disabled}
        field_key={key}
        hide_labels={this.hide_labels}
        key={key}
        node={child}
        onchange={this.onchange}
        parent={node}
        path={child_path(this.path, key)}
      />
    )
    // A cell of a line grid, spanning the field's shares.
    const span = (key: string, child: AnyNode) => (
      <div className={col_span(key)} key={key}>
        {dispatch(key, child, true)}
      </div>
    )
    const visible = Items(node.results).filter(([key]) => is_visible(key, this.ctx)) as [
      string,
      AnyNode,
    ][]

    // A compact array entry: its fields are cells in the array's own grid, so
    // emit them flat and let that grid place them.
    if (this.cell) return <>{visible.map(([key, child]) => span(key, child))}</>

    // Otherwise every run of consecutive inline fields becomes one line grid
    // (see `line_columns`); block fields break the run and take their own line.
    const lines: JSX.Element[] = []
    let run: [string, AnyNode][] = []
    const flush = () => {
      if (!run.length) return
      lines.push(
        <div
          className="grid items-start gap-x-2 gap-y-3"
          key={`line-${lines.length}`}
          style={line_columns(run.map(([key]) => key))}
        >
          {run.map(([key, child]) => span(key, child))}
        </div>
      )
      run = []
    }
    for (const [key, child] of visible) {
      if (is_block(child)) {
        flush()
        lines.push(dispatch(key, child, false))
      } else {
        if (starts_line(key)) flush()
        run.push([key, child])
      }
    }
    flush()
    return <>{lines}</>
  },
})

const ArrayField = CreateRenderFunction({
  mixins: [NodeMixin, FieldMixin, ViewMixin, ArrayMixin] as const,
  computed: {
    // A short all-leaf row object renders as a spreadsheet-like grid (one
    // shared header, one row per entry) instead of stacked detail blocks.
    is_compact(): boolean {
      const node = this.node!
      const first = node.results[0]
      return (
        this.compact_mode ||
        (!!first &&
          first.enum === FormType.OBJECT &&
          Length(first.results) <= 5 &&
          Values(first.results).every(is_leaf))
      )
    },
  },
  methods: {
    controls(index: number) {
      const node = this.node!
      return (
        node.min !== node.max && (
          <ArrayControls
            can_add={node.results.length < node.max && !this.disabled}
            can_remove={node.results.length > node.min && !this.disabled}
            on_add={() => this.form_add(index + 1)}
            on_remove={() => this.form_remove(index)}
          />
        )
      )
    },
    form_add(index: number) {
      const node = this.node!
      if (node.results.length < node.max) {
        const next = inputs(node)
        next.splice(index, 0, undefined)
        node.validate(next)
        this.onchange?.()
      }
    },
    form_remove(index: number) {
      const node = this.node!
      if (node.results.length > node.min) {
        node.validate(inputs(node).filter((_, i) => i !== index))
        this.onchange?.()
      }
    },
  },
  render() {
    const node = this.node!
    const forward = {
      className: this.className,
      compact_mode: this.compact_mode,
      ctx: this.ctx,
      disabled: this.disabled,
      onchange: this.onchange,
      parent: node,
    }
    const first = node.results[0] as ObjectForm<Record<string, unknown>> | undefined

    // No entries: the -/+ alone on a line of their own, to add the first
    if (!first)
      return <div className={`relative h-6 basis-full ${this.className}`}>{this.controls(-1)}</div>

    if (this.is_compact) {
      // A spreadsheet grid: one header line of labels over N rows of data,
      // every field a single track. Header and data are two grids sharing the
      // same N-column template, so their columns line up exactly at any width.
      // The -/+ controls sit absolute over the header line (see ArrayControls).
      const visible_keys = Items(first.results)
        .filter(([key]) => is_visible(key, this.ctx))
        .map(([key]) => key)
      // Same shares as an inline line, so its columns line up with the lines
      // around it.
      const template = line_columns(visible_keys)
      return (
        <div className={`basis-full ${this.className}`}>
          <div className="relative">
            {/* The -/+ sits absolute over the header line, past the last
                (short) label — so the data grid below stays full width and
                its columns line up with the header. */}
            {this.controls(node.results.length - 1)}
            <FieldGroup className="grid gap-x-2" style={template}>
              {visible_keys.map(key => (
                <div className={col_span(key)} key={key}>
                  {!this.hide_labels && (
                    <DjLabel field_id={key} path={child_path(this.path, 0, key)} />
                  )}
                </div>
              ))}
            </FieldGroup>
          </div>
          <FieldGroup className="mt-1 grid gap-x-2 gap-y-3" style={template}>
            {node.results.map((item, index) => (
              <Dispatch
                {...forward}
                cell={true}
                hide_labels={true}
                key={index}
                node={item}
                path={child_path(this.path, index)}
              />
            ))}
          </FieldGroup>
        </div>
      )
    }

    return (
      <div className={`relative flex basis-full flex-col ${this.className}`}>
        {node.results.map((item, index) => {
          const label_node = (item as ObjectForm<Record<string, unknown>>).results[LABEL_KEY] as
            | AtomForm<unknown>
            | undefined
          return (
            <details key={index} open>
              <summary className="relative my-3 flex list-none items-center border-b pb-1 text-lg text-muted-foreground [&::-webkit-details-marker]:hidden">
                <input
                  className="w-1/4 border-0 bg-transparent p-0 text-lg text-muted-foreground outline-none"
                  disabled={this.disabled}
                  onBlur={(e: Event) => label_node?.validate((e.target as HTMLInputElement).value)}
                  placeholder={`#${index + 1}`}
                  type="text"
                  value={String(label_node?.input ?? "")}
                />
                {this.controls(index)}
              </summary>
              <div className={`${FORM_STACK} mb-2`}>
                <Dispatch
                  {...forward}
                  hide_labels={this.hide_labels}
                  node={item}
                  path={child_path(this.path, index)}
                />
              </div>
            </details>
          )
        })}
      </div>
    )
  },
})

// A discriminated union: a select over the variant tags, then the selected
// variant's fields. Switching re-validates with that variant's own kept input.
const EnumField = CreateRenderFunction({
  mixins: [NodeMixin, FieldMixin, IDMixin, ViewMixin, EnumMixin] as const,
  render() {
    const node = this.node!
    const id = dom_id(this)
    const selected = node.selected
    return (
      <>
        <FieldShell
          className={this.className}
          error={selected ? undefined : node.error}
          field_key={this.field_key}
          hide_labels={this.hide_labels}
          path={id}
        >
          <DjSelect
            choices={RecordCreate(Keys(node.results).map(key => [key, node.label(key)] as const))}
            disabled={this.disabled}
            dom_id={id}
            error={selected ? undefined : node.error}
            input={selected}
            onselect={key =>
              void node.validate({ ...(node.results[key as string].input as object), enum: key })
            }
          />
        </FieldShell>
        {selected ? (
          <ObjectField
            className={this.className}
            compact_mode={this.compact_mode}
            ctx={this.ctx}
            disabled={this.disabled}
            hide_labels={this.hide_labels}
            node={node.results[selected]}
            onchange={this.onchange}
            path={child_path(this.path, selected)}
          />
        ) : undefined}
      </>
    )
  },
})

export const Dispatch = CreateRenderFunction({
  mixins: [NodeMixin, FieldMixin, IDMixin, ViewMixin, FormMixin] as const,
  render() {
    const node = this.node
    if (!node) return undefined
    if (this.field_key && !is_visible(this.field_key, this.ctx)) return undefined
    const forward = KeyTake(this, [
      "cell",
      "className",
      "compact_mode",
      "ctx",
      "disabled",
      "field_key",
      "hide_labels",
      "onchange",
      "parent",
      "path",
    ])
    const Custom = CUSTOM_CONTROLLERS[this.field_key]
    if (Custom) {
      return (
        <FieldShell
          cell={this.cell}
          className={this.className}
          error={node.error}
          field_key={this.field_key}
          hide_labels={this.hide_labels}
          path={dom_id(this)}
        >
          <Custom {...forward} node={node} />
        </FieldShell>
      )
    }
    return FullMethodDispatcher<[AnyNode], JSX.Element>({
      [FormType.ATOM]: n => <AtomField {...forward} node={n} />,
      [FormType.CHOICE]: n => <ChoiceField {...forward} node={n} />,
      [FormType.OBJECT]: n => <ObjectField {...forward} node={n} />,
      [FormType.ARRAY]: n => <ArrayField {...forward} node={n} />,
      [FormType.ENUM]: n => <EnumField {...forward} node={n} />,
    })(node)
  },
})
